import { useMemo } from "react";

import clsx from "clsx";
import { isNil } from "lodash";

import { type TableState } from "@features/sales-v4/types";
import { type BenchmarkIndicatorRow } from "@features/universal-benchmarks";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import { type SalesMetricRow } from "@north-beam/nb-common";
import { type CellContext } from "@tanstack/react-table";

import { BenchmarkBadge } from "../benchmark-badge";
import { getMetricComparison } from "../utils";

import { Tooltip } from "./tooltip";

type Props<TData> = {
  metric: SalesMetricV2;
  cell: CellContext<TData, unknown>;
  value?: string | number;
  tableState: TableState;
  benchmarkKpis: BenchmarkIndicatorRow[] | null;
  rowItemName: string;
  tooltipTableMetrics: {
    metricBlended: SalesMetricV2;
    metricFt: SalesMetricV2;
    metricRtn: SalesMetricV2;
  };
};

export const CellRoasFormatter = <TData extends SalesMetricRow>({
  cell,
  metric,
  value,
  tableState,
  benchmarkKpis,
  rowItemName,
  tooltipTableMetrics,
}: Props<TData>) => {
  const row = cell.row.original as SalesMetricRow;
  const { metrics, comparisonMetrics } = row;

  const benchmarkKpi = useMemo(() => {
    return benchmarkKpis?.find((e) => e.metricId === metric.id);
  }, [benchmarkKpis, metric]);

  return (
    <Tooltip
      row={cell.row.original}
      tableState={tableState}
      rowItemName={rowItemName}
      metric={metric}
      tooltipTableMetrics={tooltipTableMetrics}
      benchmarkKpis={benchmarkKpis}
    >
      <div
        className={clsx(
          "border-r-1 border-x-v2-tertiary text-v2-primary px-4 py-2.5 bg-inherit",
          "font-mono text-xs flex items-center justify-end h-full",
        )}
      >
        <div
          className={clsx("flex justify-end gap-1", {
            "flex-col items-end": tableState.showPercentChange,
            "items-center": !tableState.showPercentChange,
          })}
        >
          {benchmarkKpi &&
          (cell?.row?.original?.metrics?.spend || 0) > 0 &&
          benchmarkKpis?.some((kpi) => kpi.target > 0) ? (
            <BenchmarkBadge benchmarkKpi={benchmarkKpi}>
              {!isNil(value) ? value : String(cell.getValue())}
            </BenchmarkBadge>
          ) : (
            <>{!isNil(value) ? value : String(cell.getValue())}</>
          )}
          {getMetricComparison(
            metric,
            metrics,
            comparisonMetrics,
            tableState.showPercentChange,
          )}
        </div>
      </div>
    </Tooltip>
  );
};
